<template>
  <v-container class="py-6" max-width="1200">
    <!-- Header -->
    <div class="d-flex align-center mb-6">
      <v-btn variant="text" size="small" prepend-icon="mdi-arrow-left" to="/admin/analysis" class="mr-3">
        Back
      </v-btn>
      <v-icon color="primary" size="28" class="mr-3">mdi-chess-knight</v-icon>
      <div>
        <h1 class="text-h5 font-weight-bold mb-0">Game Reconstruction</h1>
        <p class="text-caption text-medium-emphasis mb-0">{{ meta.video }} — {{ formatTime(meta.duration_sec) }}</p>
      </div>
      <v-spacer />
      <v-btn
        v-if="textLog"
        variant="outlined"
        size="small"
        prepend-icon="mdi-download"
        @click="downloadLog"
      >
        Export Log
      </v-btn>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="text-center py-12">
      <v-progress-circular indeterminate color="primary" />
    </div>

    <template v-else>
      <!-- ── Stats row ──────────────────────────────────────────────────────── -->
      <v-row class="mb-6">
        <v-col cols="6" sm="3">
          <div class="stat-box">
            <div class="stat-value text-primary">{{ summary.total_rolls ?? 0 }}</div>
            <div class="stat-label">Total rolls</div>
          </div>
        </v-col>
        <v-col cols="6" sm="3">
          <div class="stat-box">
            <div class="stat-value text-success">{{ summary.double_dice_rolls ?? 0 }}</div>
            <div class="stat-label">Double dice</div>
          </div>
        </v-col>
        <v-col cols="6" sm="3">
          <div class="stat-box">
            <div class="stat-value">{{ summary.player1_rolls ?? 0 }}</div>
            <div class="stat-label">Player 1 rolls</div>
          </div>
        </v-col>
        <v-col cols="6" sm="3">
          <div class="stat-box">
            <div class="stat-value">{{ summary.player2_rolls ?? 0 }}</div>
            <div class="stat-label">Player 2 rolls</div>
          </div>
        </v-col>
      </v-row>

      <!-- ── Timeline ───────────────────────────────────────────────────────── -->
      <v-card variant="outlined" class="mb-6">
        <v-card-title class="pa-4 pb-2 d-flex align-center">
          <v-icon class="mr-2" size="18">mdi-timeline</v-icon>
          Roll Timeline
          <span class="text-caption text-medium-emphasis ml-2">— click a dot to jump to that roll</span>
        </v-card-title>
        <v-divider />
        <v-card-text class="pa-4">
          <div class="timeline-wrapper">
            <!-- Track -->
            <div class="timeline-track">
              <!-- Roll markers -->
              <div
                v-for="roll in timeline"
                :key="roll.roll_number"
                class="timeline-dot"
                :class="{
                  'dot-p1':      roll.player === 1,
                  'dot-p2':      roll.player === 2,
                  'dot-unknown': roll.player === null,
                  'dot-single':  roll.dice_count === 1,
                  'dot-active':  selectedRoll?.roll_number === roll.roll_number,
                }"
                :style="{ left: roll.position_pct + '%' }"
                :title="`Roll #${roll.roll_number} at ${formatTime(roll.timestamp_sec)}`"
                @click="selectRoll(roll)"
              />
            </div>

            <!-- Time labels -->
            <div class="timeline-labels">
              <span>0:00</span>
              <span>{{ formatTime(meta.duration_sec * 0.25) }}</span>
              <span>{{ formatTime(meta.duration_sec * 0.5) }}</span>
              <span>{{ formatTime(meta.duration_sec * 0.75) }}</span>
              <span>{{ formatTime(meta.duration_sec) }}</span>
            </div>
          </div>

          <!-- Legend -->
          <div class="d-flex gap-4 mt-3">
            <div class="d-flex align-center gap-1">
              <div class="legend-dot dot-p1" /><span class="text-caption">Player 1</span>
            </div>
            <div class="d-flex align-center gap-1">
              <div class="legend-dot dot-p2" /><span class="text-caption">Player 2</span>
            </div>
            <div class="d-flex align-center gap-1">
              <div class="legend-dot dot-unknown" /><span class="text-caption">Unknown player</span>
            </div>
            <div class="d-flex align-center gap-1">
              <div class="legend-dot dot-single" /><span class="text-caption">Single die</span>
            </div>
          </div>
        </v-card-text>
      </v-card>

      <!-- ── Selected roll detail ───────────────────────────────────────────── -->
      <v-card v-if="selectedRoll" variant="tonal" color="primary" class="mb-6">
        <v-card-text class="pa-4">
          <div class="d-flex align-center gap-4">
            <div>
              <div class="text-h6 font-weight-bold">Roll #{{ selectedRoll.roll_number }}</div>
              <div class="text-caption">{{ formatTime(selectedRoll.timestamp_sec) }} — frame {{ selectedRoll.frame_idx }}</div>
            </div>
            <div class="d-flex gap-2">
              <DiceFace
                v-for="(pip, i) in (selectedRoll.pip_values || Array(selectedRoll.dice_count).fill(null))"
                :key="i"
                :value="pip || 0"
                :size="40"
                :color="pip ? 'currentColor' : '#666'"
              />
            </div>
            <div v-if="selectedRoll.player" class="ml-auto">
              <v-chip :color="selectedRoll.player === 1 ? 'primary' : 'success'" variant="flat">
                Player {{ selectedRoll.player }}
              </v-chip>
            </div>
          </div>
        </v-card-text>
      </v-card>

      <!-- ── Roll list + Text log tabs ──────────────────────────────────────── -->
      <v-card variant="outlined">
        <v-tabs v-model="tab" color="primary">
          <v-tab value="rolls">
            <v-icon start size="16">mdi-format-list-numbered</v-icon>
            Roll List
          </v-tab>
          <v-tab value="log">
            <v-icon start size="16">mdi-text</v-icon>
            Text Log
          </v-tab>
        </v-tabs>
        <v-divider />

        <v-window v-model="tab">
          <!-- Roll list -->
          <v-window-item value="rolls">
            <v-table density="compact" fixed-header height="500">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Time</th>
                  <th>Player</th>
                  <th>Dice</th>
                  <th>Values</th>
                </tr>
              </thead>
              <tbody>
                <template v-for="roll in rolls" :key="roll.roll_number">
                  <tr v-if="roll.is_new_game">
                    <td colspan="5" class="new-game-row">— New game —</td>
                  </tr>
                  <tr
                    class="roll-row"
                    :class="{
                      'roll-p1': roll.player === 1,
                      'roll-p2': roll.player === 2,
                      'roll-selected': selectedRoll?.roll_number === roll.roll_number,
                    }"
                    @click="selectRoll(roll)"
                  >
                    <td class="text-caption text-medium-emphasis">{{ roll.roll_number }}</td>
                    <td class="text-caption">{{ formatTime(roll.timestamp_sec) }}</td>
                    <td>
                      <v-chip
                        v-if="roll.player"
                        size="x-small"
                        :color="roll.player === 1 ? 'primary' : 'success'"
                        variant="tonal"
                      >P{{ roll.player }}</v-chip>
                      <span v-else class="text-caption text-disabled">?</span>
                    </td>
                    <td>
                      <div class="d-flex gap-1">
                        <DiceFace
                          v-for="n in roll.dice_count"
                          :key="n"
                          :value="roll.pip_values?.[n-1] || 0"
                          :size="18"
                          :color="roll.pip_values?.[n-1] ? 'currentColor' : '#555'"
                        />
                      </div>
                    </td>
                    <td class="text-caption font-weight-bold">
                      {{ roll.pip_values?.length ? roll.pip_values.join(' + ') : '—' }}
                    </td>
                  </tr>
                </template>
              </tbody>
            </v-table>
          </v-window-item>

          <!-- Text log -->
          <v-window-item value="log">
            <div class="log-box pa-4">
              <pre class="log-text">{{ textLog }}</pre>
            </div>
          </v-window-item>
        </v-window>
      </v-card>
    </template>
  </v-container>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import api from '../../services/api'
import DiceFace from '../../components/DiceFace.vue'

const route = useRoute()
const jobId = route.params.jobId || route.query.jobId

const loading     = ref(true)
const tab         = ref('rolls')
const meta        = ref({})
const summary     = ref({})
const rolls       = ref([])
const timeline    = ref([])
const textLog     = ref('')
const selectedRoll = ref(null)

async function load() {
  loading.value = true
  try {
    const { data } = await api.get(`/training/videos/analyze/reconstruct?jobId=${jobId}`)
    meta.value     = data.metadata  || {}
    summary.value  = data.summary   || {}
    rolls.value    = data.rolls     || []
    timeline.value = data.timeline  || []
    textLog.value  = data.text_log  || ''
  } catch (e) {
    console.error('Failed to load reconstruction', e)
  } finally {
    loading.value = false
  }
}

function selectRoll(roll) {
  selectedRoll.value = roll
  // Also switch to rolls tab so user sees the list
}

function formatTime(sec) {
  if (!sec) return '0:00'
  const h = Math.floor(sec / 3600)
  const m = Math.floor((sec % 3600) / 60)
  const s = Math.floor(sec % 60)
  if (h > 0) return `${h}:${m.toString().padStart(2,'0')}:${s.toString().padStart(2,'0')}`
  return `${m}:${s.toString().padStart(2,'0')}`
}

function downloadLog() {
  const blob = new Blob([textLog.value], { type: 'text/plain' })
  const url  = URL.createObjectURL(blob)
  const a    = document.createElement('a')
  a.href     = url
  a.download = `reconstruction_${jobId}.txt`
  a.click()
  URL.revokeObjectURL(url)
}

onMounted(load)
</script>

<style scoped>
.stat-box {
  text-align: center;
  padding: 16px;
  background: rgba(var(--v-theme-surface-variant), 0.4);
  border-radius: 10px;
}
.stat-value {
  font-size: 1.8rem;
  font-weight: 700;
  line-height: 1;
  margin-bottom: 4px;
}
.stat-label {
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  opacity: 0.6;
}

/* Timeline */
.timeline-wrapper { padding: 8px 0; }
.timeline-track {
  position: relative;
  height: 24px;
  background: rgba(var(--v-theme-on-surface), 0.08);
  border-radius: 12px;
  margin-bottom: 8px;
}
.timeline-dot {
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 8px;
  height: 8px;
  border-radius: 50%;
  cursor: pointer;
  transition: transform 0.1s;
}
.timeline-dot:hover { transform: translate(-50%, -50%) scale(1.8); }
.timeline-dot.dot-active { transform: translate(-50%, -50%) scale(2); }
.dot-p1      { background: rgb(var(--v-theme-primary)); }
.dot-p2      { background: #16a34a; }
.dot-unknown { background: #6b7280; }
.dot-single  { background: #d97706; width: 5px; height: 5px; }

.timeline-labels {
  display: flex;
  justify-content: space-between;
  font-size: 0.65rem;
  opacity: 0.5;
}

.legend-dot {
  width: 10px; height: 10px;
  border-radius: 50%;
}

/* Roll table */
.roll-row { cursor: pointer; transition: background 0.1s; }
.roll-row:hover     { background: rgba(var(--v-theme-primary), 0.05); }
.roll-p1            { border-left: 3px solid rgb(var(--v-theme-primary)); }
.roll-p2            { border-left: 3px solid #16a34a; }
.roll-selected      { background: rgba(var(--v-theme-primary), 0.1) !important; }
.new-game-row {
  text-align: center;
  font-size: 0.72rem;
  color: rgba(var(--v-theme-on-surface), 0.4);
  padding: 4px;
  background: rgba(var(--v-theme-on-surface), 0.03);
}

/* Log */
.log-box { max-height: 500px; overflow-y: auto; }
.log-text {
  font-family: monospace;
  font-size: 0.75rem;
  line-height: 1.7;
  white-space: pre-wrap;
  color: rgba(var(--v-theme-on-surface), 0.8);
}
</style>
